import type { RefObject } from "react";
import type {
  PendingScrollRequest,
  ScrollBottomOptions,
  ScrollTargetOptions,
} from "./scroll-controller-types";

interface FlushPendingScrollRequestInput {
  pendingScrollRef: RefObject<PendingScrollRequest | null>;
  messageElementsRef: RefObject<Map<string, HTMLElement>>;
  versionElementsRef: RefObject<Map<string, HTMLElement>>;
  scrollToBottom: (opts?: ScrollBottomOptions) => void;
  scrollToMessageId: (messageId: string, opts?: ScrollTargetOptions) => void;
  scrollToVersionId: (versionShortId: string, opts?: ScrollTargetOptions) => void;
}

type PendingScrollFlusher = (input: FlushPendingScrollRequestInput) => boolean;

const PENDING_SCROLL_FLUSHERS: readonly PendingScrollFlusher[] = [
  flushPendingBottomScroll,
  flushPendingMessageScroll,
  flushPendingVersionScroll,
];

export function flushPendingScrollRequest(
  input: FlushPendingScrollRequestInput,
): void {
  if (!input.pendingScrollRef.current) return;

  PENDING_SCROLL_FLUSHERS.some((flushPendingScroll) =>
    flushPendingScroll(input),
  );
}

function flushPendingBottomScroll({
  pendingScrollRef,
  scrollToBottom,
}: FlushPendingScrollRequestInput): boolean {
  const pending = pendingScrollRef.current;
  if (!pending?.toBottom) return false;

  pendingScrollRef.current = null;
  scrollToBottom({ instant: pending.instant });
  return true;
}

function flushPendingMessageScroll({
  pendingScrollRef,
  messageElementsRef,
  scrollToMessageId,
}: FlushPendingScrollRequestInput): boolean {
  const pending = pendingScrollRef.current;
  const targetMessageId = pending?.targetMessageId;
  if (!targetMessageId || !messageElementsRef.current.has(targetMessageId)) {
    return false;
  }

  pendingScrollRef.current = null;
  scrollToMessageId(targetMessageId, {
    instant: pending.instant,
    offsetTop: pending.offsetTop,
  });
  return true;
}

function flushPendingVersionScroll({
  pendingScrollRef,
  versionElementsRef,
  scrollToVersionId,
}: FlushPendingScrollRequestInput): boolean {
  const pending = pendingScrollRef.current;
  const targetVersionId = pending?.targetVersionId;
  if (!targetVersionId || !versionElementsRef.current.has(targetVersionId)) {
    return false;
  }

  pendingScrollRef.current = null;
  scrollToVersionId(targetVersionId, {
    instant: pending.instant,
    offsetTop: pending.offsetTop,
  });
  return true;
}
